import React, { useMemo } from 'react';
import { BookingRecord, BookingRecordSkeleton } from '../BookingRecord/BookingRecord';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { BookingRecordProps } from './GuestHotelBookingsList.types';
import { EmptyState } from '../EmptyState';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { AccommodationPlaceType, TravelOfferReservationStatus } from '@graphql/generated';
import { NonJoyBookingsWarning } from '../NonJoyBookingsWarning';
import { Skeleton } from '@shared/components/Skeleton';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGuestHotelBookingsListTelemetry } from './GuestHotelBookingList.telemetry';
import { routePaths } from '@apps/admin/route.paths';
import { pxToRem } from '@withjoy/joykit/theme';

const GuestHotelBookingsListSkeleton = () => {
  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        flexDirection="column"
        gap={2}
        marginTop={6}
        marginBottom={6}
      >
        <Skeleton
          typographyVariant={'hed4'}
          width={'45%'}
          placeholder={<Box width="100%">&nbsp;</Box>}
        />
        <Skeleton
          typographyVariant={'body1'}
          width={'65%'}
          placeholder={<Box width="100%">&nbsp;</Box>}
        />
      </Flex>
      <Flex
        width="100%"
        alignItems="center"
        flexDirection="column"
        gap={5}
      >
        <BookingRecordSkeleton />
        <BookingRecordSkeleton />
        <BookingRecordSkeleton />
      </Flex>
    </>
  );
};

export const GuestHotelBookingsList = () => {
  const { tq } = useTranslation('websiteEditor');
  const { bookingDetailsData, loadingBookingDetails: loadingBookingDetails, accommodationsData } = useAccommodationsContext();

  const router = useRouterHelper();
  const history = useHistory();
  const telemetry = useGuestHotelBookingsListTelemetry();

  const bookingRecords = useMemo(() => {
    if (bookingDetailsData?.travelReservationBookingDetails) {
      const records: BookingRecordProps[] = [];
      bookingDetailsData.travelReservationBookingDetails.forEach(booking => {
        const { joyPlace } = booking;
        const { lat, long, ...restOfJoyPlace } = joyPlace;
        booking.bookingInfo?.forEach(bookingInfo => {
          if (bookingInfo.status !== TravelOfferReservationStatus.cancelled) {
            records.push({
              bookingInfo: { ...bookingInfo, bookingTime: new Date(bookingInfo.bookingDate).getTime() },
              joyPlace: { ...restOfJoyPlace, location: { latitude: lat, longitude: long } },
            });
          }
        });
      });

      return records.sort((a, b) => b.bookingInfo.bookingTime - a.bookingInfo.bookingTime);
    }
    return [];
  }, [bookingDetailsData]);

  const nonJoyHotelsCount = useMemo(
    () => accommodationsData?.accommodationPlacesV2?.filter(place => place.type !== AccommodationPlaceType.hotel)?.length || 0,
    [accommodationsData],
  );

  const handleOnClickRemindGuestsBook = useEventCallback(() => {
    telemetry.remindOtherGuestsToBookClicked();
    history.push(
      router.buildRelativeUrl(`${routePaths.hotelReminder.path}/create`, {
        utm_medium: 'web',
        utm_source: 'accommodations_admin_page',
        utm_campaign: 'remind_other_guests_to_book',
      }),
    );
  });

  if (loadingBookingDetails) {
    return <GuestHotelBookingsListSkeleton />;
  }

  if (bookingRecords.length === 0) {
    return (
      <EmptyState
        imageAltText={tq`Guests Bookings`}
        title={tq`Guests haven’t booked yet`}
        buttonText={tq`Remind Guests to Book`}
        onButtonClick={handleOnClickRemindGuestsBook}
        imageSrc="https://withjoy.com/assets/public/travel/guests-bookings.png"
        subtitle={tq`When they do, you’ll see their hotel bookings here. Nearby hotels fill up quickly, so send a reminder to help guests lock in the best rates.`}
      >
        {nonJoyHotelsCount > 0 && <NonJoyBookingsWarning nonJoyHotelsCount={nonJoyHotelsCount} />}
      </EmptyState>
    );
  }

  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        flexDirection="column"
        gap={2}
        padding={6}
        textAlign={'center'}
      >
        <TextV2 typographyVariant="hed4">{tq`Where Your Guests Are Staying`}</TextV2>
        {/* The maxWidth of 340px ensures the text remains readable and visually balanced on small screens, per design guidelines. */}
        <TextV2
          typographyVariant="body1"
          maxWidth={{ _: pxToRem(340), sm2: '100%' }}
        >{tq`See the bookings your guests have made on your site.`}</TextV2>
        <ButtonV2
          intent="newPrimary"
          shape="rounded"
          marginTop={4}
          onClick={handleOnClickRemindGuestsBook}
        >
          {tq`Remind Other Guests to Book`}
        </ButtonV2>
      </Flex>
      <Box>
        {nonJoyHotelsCount > 0 && (
          <Box
            width="100%"
            marginBottom={5}
            paddingX={6}
          >
            <NonJoyBookingsWarning nonJoyHotelsCount={nonJoyHotelsCount} />
          </Box>
        )}
        {bookingRecords.map(bookingRecord => (
          <Flex
            key={bookingRecord.bookingInfo?.id}
            width="100%"
            justifyContent="center"
            marginBottom={5}
          >
            <BookingRecord {...{ bookingInfo: bookingRecord.bookingInfo, joyPlace: bookingRecord.joyPlace }} />
          </Flex>
        ))}
      </Box>
    </>
  );
};
